import React from 'react'
import { Animated, Easing, Platform, Pressable, View } from 'react-native'
import { useReducedMotion } from 'react-native-reanimated'
import { keyboardFitTransform, KEYBOARD_FIT_MS } from '../../lib/inlineChat'
import { MapView } from './MapView'
import { MapControlsBarSlot } from './MapControlsBar'
import type { HydratedGameState } from '../../lib/gameState'
import type { PanOffset } from '../../lib/mapZoom'
import type { GameMapSceneConfig } from './gameMapScene'
import { useMapDimensions } from '../../lib/useMapDimensions'
import { desktopCanvasBottomSpace } from '../../lib/mapDimensions'
import { usePortraitMapReserves } from '../../hooks/usePortraitMapReserves'
import { tipsFitTransform } from '../../lib/portraitMapLayout'
import { usePortraitMapFit } from '../../hooks/useGameMapChrome'
import { useTheme } from '../../hooks/useTheme'
import type { OverlayModel } from '../../lib/ruleOverlayModel'
import { overlayPalette } from '../../lib/rulesOverlayTheme'
import { RulesOverlayBoardLayer } from './rules/RulesOverlayBoardLayer'
import { RulesOverlayLegend } from './rules/RulesOverlayLegend'
import { RulesOverlayHint, RulesOverlayTitle } from './rules/RulesOverlayChrome'

interface GameMapHostProps {
  visible?: boolean
  state: HydratedGameState
  viewerSlot: number
  isLandscape: boolean
  isDesktop: boolean
  mapConfig: GameMapSceneConfig
  actionPanel: React.ReactNode
  mapControlsBar?: React.ReactNode
  /** Optional opt-in help strip. Portrait: on top of the map controls bar. */
  tutorialHelp?: React.ReactNode
  mapRightOverlay: React.ReactNode
  regionToggleNode: React.ReactNode
  regionToggleCorner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  zoomToggleNode: React.ReactNode
  zoomToggleCorner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  zoomEnabled: boolean
  zoomScale: number
  panOffset: PanOffset
  onPanChange: (next: PanOffset) => void
  onZoomScaleChange: (scale: number) => void
  viewerJoinedAt: number
  /**
   * Portrait inline chat with the keyboard up: the soft keyboard's top edge
   * (window coords). The map controls bar + chat input slide up over the
   * map's bottom edge to sit on the keyboard. null = normal layout.
   */
  dockKeyboardTop?: number | null
  /** Wide layouts: a strip at the top of the map pane the height of the top
   *  bar, so the map sits where it does under the snapshot banner (no jump
   *  entering or leaving a snapshot). */
  landscapeTopReserve?: number
  /** Inline chat input bar, directly beneath the map controls bar (portrait;
   *  desktop/landscape puts it in the left column — see ActiveGameView). */
  chatBar?: React.ReactNode
  /**
   * The open ★ map rules overlay: a scrim over the live board
   * (drawn inside the map's SVG, so pan and zoom keep working) and a legend
   * in the action panel's place. A tap on the map or outside the legend, ×
   * or Esc closes it.
   */
  rulesOverlay?: { model: OverlayModel; onClose: () => void } | null
  /**
   * The map bar's players filter: the selected players' or teams' territories
   * brought forward, the rest of the board darkened under the rules overlay's
   * scrim. null = no filter; an empty list darkens everything (the selection
   * holds no land the viewer can see). Play carries on normally. A rules
   * overlay, when open, takes precedence.
   */
  focusTerritoryIds?: readonly string[] | null
}

/** Isolation state of an open rules overlay; reset whenever another overlay opens. */
function useRulesOverlayState(key: string | null) {
  const [pinned, setPinned] = React.useState<string | null>(null)
  const [hovered, setHovered] = React.useState<string | null>(null)
  const [lastKey, setLastKey] = React.useState(key)
  if (key !== lastKey) {
    setLastKey(key)
    setPinned(null)
    setHovered(null)
  }
  return { pinned, setPinned, hovered, setHovered, isolated: pinned ?? hovered }
}

/**
 * Stable live-game map slot. Phase UIs render only their controls and register
 * the small interaction projection consumed here. Keeping MapView at this
 * fixed tree position preserves its geometry caches and event-animation state
 * while attack/fortify/placement panels change around it.
 */
export function GameMapHost({
  visible = true,
  state,
  viewerSlot,
  isLandscape,
  isDesktop,
  mapConfig,
  actionPanel,
  mapControlsBar,
  tutorialHelp,
  mapRightOverlay,
  regionToggleNode,
  regionToggleCorner,
  zoomToggleNode,
  zoomToggleCorner,
  zoomEnabled,
  zoomScale,
  panOffset,
  onPanChange,
  onZoomScaleChange,
  viewerJoinedAt,
  dockKeyboardTop = null,
  landscapeTopReserve = 0,
  chatBar,
  rulesOverlay = null,
  focusTerritoryIds = null,
}: GameMapHostProps) {
  // Keyboard fit (portrait inline chat). Layout never changes — a re-layout
  // is a visible jump. The map holds still; the bar stack (map bar + chat
  // input) slides up with a native-driven transform until the input sits on
  // the keyboard, covering the map's bottom edge while you type (only tall
  // maps need it). It slides back when the keyboard closes. The map's scale
  // transform is used by the tips fit. Measurements are untransformed (host
  // window top, layout sizes).
  const hostRef = React.useRef<View>(null)
  const visibleRef = React.useRef(visible)
  visibleRef.current = visible
  const [hostTop, setHostTop] = React.useState<number | null>(null)
  const [mapHeight, setMapHeight] = React.useState(0)
  const [anchorHeight, setAnchorHeight] = React.useState(0)
  const [tipsHeight, setTipsHeight] = React.useState(0)
  // The map bar's own height (portrait): the ★ sheet docks flush under it.
  const [barHeight, setBarHeight] = React.useState(0)
  const [legendHeight, setLegendHeight] = React.useState(0)
  // Rules overlay layout: the host's size, the portrait stack's top and the
  // landscape map pane's left edge (= the action column's width).
  const [hostSize, setHostSize] = React.useState({ width: 0, height: 0 })
  const [stackTop, setStackTop] = React.useState(0)
  const [paneX, setPaneX] = React.useState(0)
  // Web: measure only while the keyboard is down. The host never moves when
  // it opens, but the browser may be mid-scroll revealing the input (see
  // useKeyboardTop), and a reading taken then made the fit fall short and
  // leave the bars behind the keyboard. Native re-measures on open.
  const keyboardUpRef = React.useRef(dockKeyboardTop !== null)
  keyboardUpRef.current = dockKeyboardTop !== null
  const measureHost = React.useCallback(() => {
    if (!visibleRef.current) return
    if (Platform.OS === 'web' && keyboardUpRef.current) return
    hostRef.current?.measureInWindow((_x, y) => setHostTop(y))
  }, [])
  React.useEffect(() => {
    if (dockKeyboardTop !== null && Platform.OS !== 'web') measureHost()
  }, [dockKeyboardTop, measureHost])
  // Portrait budget (lib/portraitMapLayout): tall maps stop the controls bar
  // at the 65% line. Sizing uses the shared estimate (same as the snapshot);
  // the fit transforms use the measured host top once known.
  const portraitReserves = usePortraitMapReserves(state.map)
  const { windowHeight } = portraitReserves
  const mapTop = hostTop === null ? portraitReserves.mapTop : hostTop - portraitReserves.topOverlap
  // Never skip the fit for want of a measurement: mapTop falls back to the
  // estimate, so the bars always land on the keyboard.
  const keyboardFit = !isLandscape && chatBar && dockKeyboardTop !== null && mapHeight > 0 && anchorHeight > 0
    ? keyboardFitTransform({ hostTop: mapTop, mapHeight, barsHeight: anchorHeight, keyboardTop: dockKeyboardTop })
    : { scale: 1, shift: 0 }
  const tipsFit = !isLandscape
    ? tipsFitTransform({ mapTop, mapHeight, tipsHeight, windowHeight })
    : { scale: 1, shift: 0 }
  const { recordFit } = usePortraitMapFit(state.map.id)
  React.useEffect(() => {
    if (visible) recordFit({ mapId: state.map.id, scale: tipsFit.scale, shift: tipsFit.shift, tipsHeight: isLandscape ? 0 : tipsHeight })
  }, [recordFit, state.map.id, tipsFit.scale, tipsFit.shift, tipsHeight, isLandscape, visible])
  const fit = keyboardFit.shift >= tipsFit.shift ? keyboardFit : tipsFit
  const reducedMotion = useReducedMotion()
  const mapScale = React.useRef(new Animated.Value(1)).current
  const stackShift = React.useRef(new Animated.Value(0)).current
  // Tips fade out while the chat keyboard is up (and back after), keeping
  // their space so nothing around them jumps; same timing as the bar slide.
  const keyboardUp = dockKeyboardTop !== null
  const tipsOpacity = React.useRef(new Animated.Value(keyboardUp ? 0 : 1)).current
  React.useEffect(() => {
    if (!visible) return
    if (reducedMotion) {
      tipsOpacity.setValue(keyboardUp ? 0 : 1)
      return
    }
    Animated.timing(tipsOpacity, {
      toValue: keyboardUp ? 0 : 1,
      duration: KEYBOARD_FIT_MS,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start()
  }, [keyboardUp, reducedMotion, tipsOpacity, visible])
  React.useEffect(() => {
    if (!visible) return
    if (reducedMotion) {
      mapScale.setValue(fit.scale)
      stackShift.setValue(-fit.shift)
      return
    }
    const easing = Easing.out(Easing.cubic)
    Animated.parallel([
      Animated.timing(mapScale, { toValue: fit.scale, duration: KEYBOARD_FIT_MS, easing, useNativeDriver: true }),
      Animated.timing(stackShift, { toValue: -fit.shift, duration: KEYBOARD_FIT_MS, easing, useNativeDriver: true }),
    ]).start()
  }, [fit.scale, fit.shift, mapScale, stackShift, reducedMotion, visible])
  const dimensions = useMapDimensions(state, {
    reservedBottom: isLandscape && mapControlsBar ? 44 : undefined,
    reservedTop: isLandscape && landscapeTopReserve ? landscapeTopReserve : undefined,
  })
  const vb = state.map.viewBox
  // A map with its own backdrop (MapDef.backdropColor) fills the map area with it,
  // top and bottom included, instead of paper.
  const { theme } = useTheme()
  const sea = state.map.backdropColor?.[theme === 'dark' ? 'dark' : 'light']
  const backdropStyle = sea ? { backgroundColor: sea } : undefined
  // Use a shorter desktop canvas: halve its spare vertical space so the
  // centred board rises halfway toward the top and the toolbar follows the
  // canvas bottom. The remainder of the full-height pane stays empty below.
  const desktopBottomSpace = desktopCanvasBottomSpace(dimensions, vb)
  // continuousAttackEnabled is tutorial-strip context, not a map prop. Spread
  // into MapView it changed a memo-compared prop, so toggling Continuous Attack
  // re-rendered the whole map for nothing.
  const { continuousAttackEnabled: _continuousAttack, ...mapProps } = mapConfig
  const overlayModel = visible ? rulesOverlay?.model ?? null : null
  const closeOverlay = rulesOverlay?.onClose
  const overlayState = useRulesOverlayState(overlayModel ? `${overlayModel.kind}:${state.map.id}` : null)
  const palette = overlayPalette(theme)
  const sheetLegend = !isLandscape
  // Esc closes the overlay on web.
  React.useEffect(() => {
    if (!overlayModel || !closeOverlay || Platform.OS !== 'web' || typeof window === 'undefined') return
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') closeOverlay() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [overlayModel, closeOverlay])
  // The filtered players' land, cut out of the scrim (nothing drawn, no legend).
  const focusKey = !overlayModel && visible && focusTerritoryIds ? focusTerritoryIds.join(',') : null
  const focusModel = React.useMemo<OverlayModel | null>(() => focusKey !== null ? {
    kind: 'map', title: '', rows: [], footer: [], accessibilitySummary: '',
    // Lighter than the rules overlays, so the rest of the board still reads (maintainer, 2026-11-03).
    scrimStrength: 0.7,
    marks: [{ kind: 'hole', row: 'focus', detail: false, territoryIds: focusKey ? focusKey.split(',') : [] }],
  } : null, [focusKey])
  const boardOverlay = React.useMemo(() => overlayModel ? (frame: Parameters<NonNullable<React.ComponentProps<typeof MapView>['boardOverlay']>>[0]) => (
    <RulesOverlayBoardLayer
      model={overlayModel}
      isolatedRow={overlayState.isolated}
      territoryShapeById={frame.territoryShapeById}
      regionShapeById={frame.regionShapeById}
      regionTerritoryIds={frame.regionTerritoryIds}
      viewBox={frame.viewBox}
      unitsPerPixel={frame.unitsPerPixel}
      palette={palette}
      idPrefix={frame.idPrefix}
      compact={!isDesktop}
    />
  ) : focusModel ? (frame: Parameters<NonNullable<React.ComponentProps<typeof MapView>['boardOverlay']>>[0]) => (
    <RulesOverlayBoardLayer
      model={focusModel}
      isolatedRow={null}
      territoryShapeById={frame.territoryShapeById}
      regionShapeById={frame.regionShapeById}
      regionTerritoryIds={frame.regionTerritoryIds}
      viewBox={frame.viewBox}
      unitsPerPixel={frame.unitsPerPixel}
      palette={palette}
      idPrefix={frame.idPrefix}
      compact={!isDesktop}
    />
  ) : undefined, [overlayModel, overlayState.isolated, palette, isDesktop, focusModel])
  // While the overlay is open, a tap on the map closes it instead of playing.
  const overlayPress = overlayModel && closeOverlay
    ? { onTerritoryPress: () => closeOverlay(), onBackgroundPress: () => closeOverlay() }
    : null
  const mapNode = (
    <MapView
      state={state}
      viewerSlot={viewerSlot}
      {...mapProps}
      {...overlayPress}
      boardOverlay={boardOverlay}
      rightOverlay={mapRightOverlay}
      regionToggleNode={regionToggleNode}
      regionToggleCorner={regionToggleCorner}
      zoomToggleNode={zoomToggleNode}
      zoomToggleCorner={zoomToggleCorner}
      events={state.events}
      hideOnMapZoomButtons={!!mapControlsBar}
      reservedTop={isLandscape ? (landscapeTopReserve || undefined) : portraitReserves.reservedTop}
      trimMargins={!isLandscape}
      reservedBottom={isLandscape ? (mapControlsBar ? 44 : 0) + desktopBottomSpace : portraitReserves.reservedBottom}
      zoomEnabled={zoomEnabled}
      panOffset={panOffset}
      onPanChange={onPanChange}
      zoomScale={zoomScale}
      onZoomScaleChange={onZoomScaleChange}
      viewerJoinedAt={viewerJoinedAt}
    />
  )

  // Phone sheet: from the map bar's foot to the bottom of the screen at most.
  const sheetTop = stackTop + tipsHeight + barHeight
  const sheetMaxHeight = Math.max(120, hostSize.height - sheetTop)
  const legend = overlayModel && closeOverlay ? (
    <RulesOverlayLegend
      model={overlayModel}
      palette={palette}
      sheet={sheetLegend}
      pinned={overlayState.pinned}
      onPin={overlayState.setPinned}
      onHover={overlayState.setHovered}
      onClose={closeOverlay}
      maxHeight={sheetLegend ? sheetMaxHeight : Math.max(120, hostSize.height - 64)}
      // Rounded at the foot only while it ends above the bottom of the screen.
      roundedBottom={legendHeight < sheetMaxHeight - 1}
      onLayout={(height) => setLegendHeight((prev) => (prev === height ? prev : height))}
    />
  ) : null

  return (
    <View
      ref={hostRef}
      onLayout={(event) => {
        measureHost()
        const { width, height } = event.nativeEvent.layout
        setHostSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }))
      }}
      className={isLandscape ? 'flex-1 flex-row' : 'flex-1'}
    >
      {isLandscape && actionPanel}
      {isLandscape ? (
        <View
          className="flex-1 border-l border-olive bg-paper"
          style={landscapeTopReserve && !backdropStyle ? { paddingTop: landscapeTopReserve } : undefined}
          onLayout={(event) => { const x = event.nativeEvent.layout.x; setPaneX((prev) => (prev === x ? prev : x)) }}
        >
          {/* The backdrop (MapDef.backdropColor) fills the map and the strip above it;
              below the map controls bar stays the theme's paper. */}
          <View
            className={`${isDesktop ? 'items-start' : 'flex-1 items-start justify-center'}`}
            style={backdropStyle ? [backdropStyle, landscapeTopReserve ? { paddingTop: landscapeTopReserve } : undefined] : undefined}
          >
            {mapNode}
            {overlayModel && closeOverlay ? (
              <RulesOverlayTitle model={overlayModel} palette={palette} compact={!isDesktop} top={14 + (landscapeTopReserve || 0)} onClose={closeOverlay} />
            ) : null}
            {overlayModel ? (
              <View pointerEvents="none" style={{ position: 'absolute', left: 0, right: 0, bottom: 14 }}>
                <RulesOverlayHint desktop={isDesktop} />
              </View>
            ) : null}
          </View>
          <MapControlsBarSlot state={state}>{mapControlsBar}</MapControlsBarSlot>
          {tutorialHelp}
        </View>
      ) : (
        // Portrait: the map scales (not re-lays-out) when tips need the room on
        // a tall map; the chat keyboard only slides the bars over it.
        // The full-width backing is the map's ocean (paper in both themes):
        // when the map scales down it narrows too, and this keeps the board
        // reading edge to edge instead of showing the screen at its sides.
        <View className="bg-paper" style={[{ marginTop: -portraitReserves.topOverlap }, backdropStyle]}>
          <Animated.View
            onLayout={(event) => { if (visible) setMapHeight(event.nativeEvent.layout.height) }}
            style={{ transformOrigin: 'top', transform: [{ scale: mapScale }] }}
          >
            {mapNode}
          </Animated.View>
        </View>
      )}
      {/* Portrait: one always-present stack (nothing below it remounts). With
          the keyboard up the map above shrinks, so this stack's bars sit on
          the keyboard; whatever is under the input goes behind the keyboard. */}
      {!isLandscape && (
        <Animated.View
          testID={keyboardFit.shift > 0 ? 'map-keyboard-fit' : undefined}
          onLayout={(event) => { const y = event.nativeEvent.layout.y; setStackTop((prev) => (prev === y ? prev : y)) }}
          style={{ flex: 1, zIndex: 5, transform: [{ translateY: stackShift }] }}
        >
          <View onLayout={(event) => { if (visible) setAnchorHeight(event.nativeEvent.layout.height) }}>
            {/* Tips ride on top of the controls bar. */}
            <Animated.View
              onLayout={(event) => { if (visible) setTipsHeight(event.nativeEvent.layout.height) }}
              // Faded out (not removed) while the chat keyboard is up.
              pointerEvents={keyboardUp ? 'none' : 'auto'}
              style={{ opacity: tipsOpacity }}
            >
              {tutorialHelp}
            </Animated.View>
            <View onLayout={(event) => { if (visible) { const h = event.nativeEvent.layout.height; setBarHeight((prev) => (prev === h ? prev : h)) } }}>
              <MapControlsBarSlot state={state} fullWidth>{mapControlsBar}</MapControlsBarSlot>
            </View>
            {chatBar}
            {/* Keyboard up: a strip of the bar's fill hangs just below it (out
                of layout), so a sub-pixel sliver between the input and the
                keyboard shows bar, never map. */}
            {keyboardUp && chatBar ? (
              <View
                pointerEvents="none"
                testID="chat-keyboard-seam"
                className="bg-paper"
                style={{ position: 'absolute', left: 0, right: 0, top: '100%', height: 4 }}
              />
            ) : null}
          </View>
          {actionPanel}
          {/* Rules overlay: the turn panel steps back behind the legend; a
              tap on it closes the overlay. */}
          {overlayModel && closeOverlay ? (
            <Pressable
              testID="rules-overlay-backdrop"
              accessibilityLabel="Close the rules"
              onPress={closeOverlay}
              style={{ position: 'absolute', left: 0, right: 0, bottom: 0, top: anchorHeight, backgroundColor: palette.scrim }}
            />
          ) : null}
        </Animated.View>
      )}
      {/* Landscape / desktop: the action column steps back behind the legend. */}
      {isLandscape && overlayModel && closeOverlay ? (
        <Pressable
          testID="rules-overlay-backdrop"
          accessibilityLabel="Close the rules"
          onPress={closeOverlay}
          style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: paneX, backgroundColor: palette.scrim }}
        />
      ) : null}
      {legend ? (
        <View
          pointerEvents="box-none"
          style={sheetLegend
            // Docked flush under the map bar, edge to edge (like the players filter bar).
            ? { position: 'absolute', left: 0, right: 0, top: sheetTop, zIndex: 20 }
            : { position: 'absolute', left: 10, top: 54, width: Math.max(260, paneX - 20), zIndex: 20 }}
        >
          {legend}
        </View>
      ) : null}
    </View>
  )
}
